import { Observable } from 'rxjs';
import { FilterVisualType } from '../../ui/filters/models';
import { AbstractControl, FormArray } from '@angular/forms';
import { MaskitoOptions } from '@maskito/core';
import { SelectionRange } from '@maskito/core/src/lib/types';

export const localKey = 'NEXT_LOCALE';

export type ResponseSort = {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
};

export type ResponsePageable = {
  sort: ResponseSort;
  paged: boolean;
  pageNumber: number;
  pageSize: number;
  offset: number;
};

export interface ApiResponse<T> {
  content: Array<T>;
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: ResponsePageable;
  size: number;
  sort: ResponseSort;
  totalElements: number;
  totalPages: number;
}

export enum PaymentMethodKey {
  CRYPTOCOIN = 'CRYPTOCOIN',
  CC_VISAMC = 'CC_VISAMC',
  CASH = 'CASH',
  KUNA_CODE = 'KUNA_CODE',
  TRANSFER = 'TRANSFER',
  EXCHANGE = 'EXCHANGE',
  QIWI = 'QIWI',
  VA_QR = 'VA_QR',
  SWIFT = 'SWIFT',
}

export interface PaymentMethodProperties {
  optional: string[];
  required: string[];
  requisites: PaymentMethodRequisites;
}

export interface PaymentMethodRequisites {
  iban?: string;
  receiverName?: string;
  bankSwiftCode?: string;
  bankName?: string;
  bankAddress?: string;
}

export interface PaymentMethod {
  id: string;
  orderType: string;
  productType: string;
  name: string;
  methodKey: string;
  currency: string;
  percent: number;
  fixed: number;
  scale: number;
  isCrypto?: boolean;
  verificationLevel: 0 | 1 | 2 | 3;
  minLimit: number;
  maxLimit: number;
  calculateFee: boolean;
  balance?: number;
  extra?: string;
  blockchain?: string;
  fixedFeeCurrency?:string;
  properties: PaymentMethodProperties;
  requisites?: PaymentMethodRequisites;
}

export enum UserType {
  business = 'BUSINESS',
  regular = 'REGULAR',
}

export enum CurrencyType {
  crypto = 'CRYPTO',
  fiat = 'FIAT',
}

export enum Currency {
  usd = 'USD',
  eur = 'EUR',
  uah = 'UAH',
  idr = 'IDR',
  kzt = 'KZT',
  btc = 'BTC',
  eth = 'ETH',
  usdt = 'USDT',
  matic = 'MATIC',
  bnb = 'BNB',
  usdc = 'USDC',
  busd = 'BUSD',
  dai = 'DAI',
  trx = 'TRX',
  try = 'TRY',
  sol = 'SOL',
  azn = 'AZN',
  gbp = 'GBP',
  pepe = 'PEPE',
}

export interface RegistrationData {
  captcha: string;
  companyName?: string;
  password: string;
  email: string;
  totp: string;
  userType: string;
  utm?: object;
}

export interface TempUserData {
  captcha: string;
  password: string;
  email: string;
}

export interface EmailVerification {
  codeVerification?: {
    vid: string;
    expiredAt: string;
  };
  emailActivated: boolean;
}

export enum OIDCType {
  facebook = 'facebook',
  google = 'google',
}

export interface EmailVerificationData {
  email: string;
  captcha: string;
}
export interface AuthenticatedUser {
  id: string;
  userId: string;
  merchantId: string;
  enabled: boolean;
  groupId: string;
  email: string;
  verificationLevel: string;
  sessionDuration: string;
  referralCode: string;
  mfaEnabled: boolean;
  locale: string;
  amlEnabled: boolean;
  userType: string;
  codeVerification: {
    vid: string;
    expiredAt: string;
    resendAfterSeconds: number;
  };
  createdAt: string;
}

export interface Provider {
  id: string;
  name: string;
  key: string;
  proxyId: string;
  enabled: boolean;
  parameters: Record<string, string>;
  proxyName?: string;
}

export interface CurrencyDataOption {
  value: string;
  label: string;
  isCrypto: boolean;
  blockchainOptions: string[];
  blockchain?: string;
}

export interface CurrencyData {
  currency: string;
  type: string;
  scale: number;
  cryptoDetails: CryptoDetail[];
}

export interface CryptoDetail {
  blockchain: string;
  addressType: string;
}

export interface WidgetCurrencyData {
  [key: string]: {
    blockchain: string;
    currency: string;
    balanceModel: string;
    networkCurrency: string;
    networkCryptoCurrency: string;
    tokenStandard: string;
  };
}

export interface OnRampCurrencyData {
  cryptoOptions: {
    [key: string]: {
      blockchain: string;
      currency: string;
      balanceModel: string;
      networkCurrency: string;
      networkCryptoCurrency: string;
      tokenStandard: string;
    };
  }
}

export enum TypeVerification {
  noVerify = 0,
  mailVerify = 1,
  phoneVerify = 2,
  documentsVerify = 3,
}

export interface FilterItem {
  value: unknown;
  text: string | Observable<string>;
}

export interface Filter {
  label: string | Observable<string>;
  list?: Observable<Array<FilterItem>>;
  name: string;
  type: FilterVisualType;
  source: Observable<Array<string>>;
  control: AbstractControl | FormArray;
}

export interface FiltersConfig {
  defaultFilters: Array<unknown>;
  getFilterList(): Array<Filter>;
}

export interface FiltersControl extends FiltersConfig {
  applyFilters(filtersState: Record<string, Array<string>>): void;
  resetFilters(): void;
  getFiltersTouched(): Observable<boolean>;
}

export interface PreferencesData {
  eqCurrency: string;
  hideBalances: boolean;
  sortType: string[];
  separation: string[];
  currency: string[];
  hideZero: string[];
}

export enum BlockchainType {
  tron = 'TRON',
  ethereum = 'ETHEREUM',
  solana = 'SOLANA',
}

export const replaceCommaToDot: MaskitoOptions = {
  mask: /^\d*[.,]?\d*$/,
  preprocessors: [
    ({ elementState, data }): { data: string; elementState: { selection: SelectionRange; value: string } } => {
      const { value, selection } = elementState;

      return {
        elementState: {
          selection,
          value: value.replace(/,/g, '.'),
        },
        data: data.replace(/,/g, '.'),
      };
    },
  ],
};
